import type { ListScope } from "@baton/core";
import { useLocation } from "react-router";

/**
 * A sprint's context (2026-10-02, by the owner's decision: Backlog "shouldn't show up as a status in
 * Sprint"): a sprint's own page, `/{ws}/sprints/{id}`, with its rows, its bulk bar and the peek opened
 * over it, and a task's page opened from one, which the navigation carries as `SPRINT_ORIGIN`.
 */
export interface SprintOrigin {
  readonly fromSprint: true;
}

/** The navigation state of a task's page opened from a sprint's page. */
const SPRINT_ORIGIN: SprintOrigin = { fromSprint: true };

/** The navigation state of a task's page opened from a collection: the sprint's context from a sprint's collection. */
export function sprintOriginOf(scope: Pick<ListScope, "cycleId">): SprintOrigin | undefined {
  return scope.cycleId === undefined ? undefined : SPRINT_ORIGIN;
}

/** The sprint whose own page a path is, or null; the sprints list and `active`, which redirects to one, are no sprint's page. */
export function sprintOfPath(pathname: string, ws: string): string | null {
  const prefix = `/${ws}/sprints/`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length);
  return rest === "" || rest === "active" || rest.includes("/") ? null : decodeURIComponent(rest);
}

function isSprintPage(pathname: string, ws: string): boolean {
  return sprintOfPath(pathname, ws) !== null;
}

function isSprintOrigin(state: unknown): boolean {
  return typeof state === "object" && state !== null && "fromSprint" in state && state.fromSprint === true;
}

/** The navigation state a task's page opened from here carries: the sprint's context on a sprint's page, none elsewhere. */
export function useSprintOrigin(ws: string): SprintOrigin | undefined {
  const { pathname } = useLocation();
  return isSprintPage(pathname, ws) ? SPRINT_ORIGIN : undefined;
}

/** Whether the screen is in a sprint's context. */
export function useSprintContext(ws: string): boolean {
  const location = useLocation();
  const state: unknown = location.state;
  return isSprintPage(location.pathname, ws) || isSprintOrigin(state);
}
